'use client';

import { useMemo, useState, useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';

import { ArrowDown } from '@/components/atoms/Icons';

/**
 * Sort hearts by year (latest first) then alphabetically by name
 */
function sortHearts(hearts) {
  return [...hearts].sort((a, b) => {
    // First, sort by year (latest first)
    const aYear = a.heartYear?.name ? Number(a.heartYear.name) : 0;
    const bYear = b.heartYear?.name ? Number(b.heartYear.name) : 0;

    if (aYear !== bYear) {
      return bYear - aYear; // Latest first
    }

    // Then, sort alphabetically by name
    const aName = (a.title || '').toLowerCase();
    const bName = (b.title || '').toLowerCase();
    return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' });
  });
}

/**
 * Filter and search hearts based on selected filters and search query
 */
function filterHearts(hearts, selectedYear, selectedArtist, searchQuery) {
  return hearts.filter((heart) => {
    // Filter by year
    if (selectedYear !== 'all') {
      const heartYear = heart.heartYear?.name ? String(heart.heartYear.name) : 'all';
      if (heartYear !== selectedYear) return false;
    }

    // Filter by artist
    if (selectedArtist !== 'all') {
      const heartArtist = heart.artistName || '';
      if (heartArtist !== selectedArtist) return false;
    }

    // Search filter (search on heart name and artist name)
    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const heartName = (heart.title || '').toLowerCase();
      const artistName = (heart.artistName || '').toLowerCase();

      // Use contains search
      if (!heartName.includes(query) && !artistName.includes(query)) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Photo card component
 */
function PhotoCard({ heart, onClick, radiusClasses }) {
  const imageUrl = heart.heroImage || heart.images?.[0];

  return (
    <div
      className={`aspect-square cursor-pointer overflow-hidden relative group flex items-center justify-center ${imageUrl ? '' : 'bg-gray-200'} ${radiusClasses}`}
      onClick={() => onClick(heart)}
    >
      {imageUrl ? (
        <Image
          src={imageUrl}
          alt={heart.name || ''}
          fill
          sizes="(max-width: 768px) 33vw, (max-width: 1200px) 20vw, 16vw"
          className="object-cover scale-[1.01] transition-transform duration-300 group-hover:scale-105 will-change-transform"
        />
      ) : (
        <Image
          src="/images/heart.jpg"
          alt={heart.name || ''}
          fill
          sizes="(max-width: 768px) 33vw, (max-width: 1200px) 20vw, 16vw"
          className="object-cover scale-[1.01] transition-transform duration-300 group-hover:scale-105 will-change-transform"
        />
      )}
    </div>
  );
}

/**
 * Empty state
 */
function EmptyState({ message = 'No Hearts Found' }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 bg-gray-100">
      <h3 className="text-lg font-medium text-gray-900 mb-2">{message}</h3>
    </div>
  );
}

/**
 * Filter dropdown component
 */
function FilterDropdown({ label, options, value, onChange }) {
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-xs font-medium text-gray-700">{label}</label>}
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="px-3.5 md:py-3 py-1 rounded-lg appearance-none border border-[#DDDDDD] bg-white w-full pr-8 md:h-12 h-11 text-base font-reguler text-[#757575]"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ArrowDown className="pointer-events-none absolute inset-y-0 right-3 m-auto" />
      </div>
    </div>
  );
}

/** Search input component */

function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative flex-1">
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-3.5 md:py-3 py-1 pr-10 md:h-12 h-11 bg-white border border-[#DDDDDD] rounded-md text-base focus:outline-none focus:ring-1 focus:ring-[#00EAFD] focus:border-transparent"
      />

      {/* Clear Button (X) - only show when there's input */}
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          className={`absolute right-4 top-1/2 cursor-pointer transform -translate-y-1/2 hover:text-gray-600`}
          aria-label="Clear search"
        >
          <svg
            className="w-4 h-4 text-[#757575]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      ) : (
        <div className={`absolute right-4 top-1/2 transform -translate-y-1/2`}>
          {/* Search Icon */}
          <svg
            className={`w-5 h-5 text-[#A8A8A8]`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      )}
    </div>
  );
}

/**
 * HeartsClient - Hearts archive with filters and search
 */

export default function HeartsClient({ initialHearts = [], archivedYears = [], artistsList = [] }) {
  const router = useRouter();
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedArtist, setSelectedArtist] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [columns, setColumns] = useState(3);
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      const cols = w >= 1280 ? 6 : w >= 1024 ? 5 : w >= 768 ? 4 : 3;

      setColumns(cols);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const getRadiusClasses = (index, totalItems) => {
    const totalRows = Math.ceil(totalItems / columns);

    const row = Math.floor(index / columns) + 1;
    const col = (index % columns) + 1;

    const isFirstRow = row === 1;
    const isLastRow = row === totalRows;

    const lastColInRow = row === totalRows ? totalItems - (totalRows - 1) * columns : columns;

    const isFirstCol = col === 1;
    const isLastCol = col === lastColInRow;

    return [
      isFirstRow && isFirstCol && 'rounded-tl-[16px]',
      isFirstRow && isLastCol && 'rounded-tr-[16px]',
      isLastRow && isFirstCol && 'rounded-bl-[16px]',
      isLastRow && isLastCol && 'rounded-br-[16px]',
    ]
      .filter(Boolean)
      .join(' ');
  };

  // Sort hearts once on mount
  const sortedHearts = useMemo(() => sortHearts(initialHearts), [initialHearts]);

  // Filter and search
  const displayHearts = useMemo(() => {
    const filtered = filterHearts(sortedHearts, selectedYear, selectedArtist, searchQuery);
    // return filtered.filter((heart) => {
    //   // Only show hearts with images
    //   return (heart.images?.length > 0 || heart.heroImage);
    // });

    return filtered;
  }, [sortedHearts, selectedYear, selectedArtist, searchQuery]);

  const handlePhotoClick = useCallback(
    (heart) => {
      router.push(`/hearts/${heart.slug}`);
    },
    [router]
  );

  // Prepare filter options
  const yearOptions = [
    { label: 'All Years', value: 'all' },
    ...archivedYears.map((year) => ({
      label: String(year),
      value: String(year),
    })),
  ];

  const artistOptions = [
    { label: 'All Artists', value: 'all' },
    ...artistsList.map((artist) => ({
      label: artist,
      value: artist,
    })),
  ];

  return (
    <div className="flex flex-col bg-[#F5F5F5] min-h-screen">
      {/* Header with filters and search */}
      <div className="lg:pb-5.5 pb-5 2xl:pt-23 xl:pt-20 lg:pt-16 md:pt-12 pt-8">
        <div className="container-wrap md:flex gap-6 items-center justify-between flex-wrap">
          <h1 className="font-semibold text-[26px] 2xl:text-[48px] lg:text-[40px] md:text-[36px] leading-[1] md:mb-0 mb-4">
            Heart Archive
          </h1>

          {/* Filters and search row */}
          <div className="flex flex-col gap-2.25 md:flex-row md:items-end">
            <div className="grid grid-cols-2 gap-2.25 md:flex md:w-auto w-full">
              <div className="w-full md:w-40">
                <FilterDropdown
                  options={yearOptions}
                  value={selectedYear}
                  onChange={setSelectedYear}
                />
              </div>

              <div className="w-full lg:w-55 md:w-50">
                <FilterDropdown
                  options={artistOptions}
                  value={selectedArtist}
                  onChange={setSelectedArtist}
                />
              </div>
            </div>

            <div className="w-full lg:w-85.75 md:w-75">
              <SearchInput
                value={searchQuery}
                onChange={(value) => {
                  setSearchQuery(value);
                  setSelectedYear('all');
                  setSelectedArtist('all');
                }}
                placeholder="Search heart name, artist name..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {displayHearts.length === 0 ? (
          <EmptyState message="No Results Found" />
        ) : (
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-0">
            {displayHearts.map((heart, index) => {
              const radiusClasses = getRadiusClasses(index, displayHearts.length);

              return (
                <PhotoCard
                  key={heart.id}
                  heart={heart}
                  onClick={handlePhotoClick}
                  radiusClasses={radiusClasses}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
